import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  DealPageActionButtonInfo,
  DealsPageHeader,
} from "./DealsPageHeader/DealsPageHeader";
import { Box, SxProps, Theme, useTheme } from "@mui/material";
import RightPanel from "../../common/rightPanel/RightPanel";
import { PopupsContext } from "../../../helpers/contexts/popupsContext";

type Props = PropsWithChildren<{
  title?: string;
  leftHeaderAction?: React.ReactNode;
  rightHeaderAction?: React.ReactNode;
  actionButtons?: DealPageActionButtonInfo[];
  sidebar?: React.ReactNode;
  sidebarTitle?: string;
  sidebarSubTitle?: string;
  sidebarHasDelimiter?: boolean;
  sidebarActionButtons?: DealPageActionButtonInfo[];
  scrollable?: boolean;
  sidebarOnClose?: () => void;
  showDealNav?: boolean;
}>;

const defaultPageWidth = 900;
const defaultHeaderHeight = 100;

export const PageWidthContext = createContext<number>(defaultPageWidth);
export const PageHeaderHeightContext =
  createContext<number>(defaultHeaderHeight);

export const DealsPageLayout = ({
  title,
  leftHeaderAction,
  rightHeaderAction,
  actionButtons,
  sidebar,
  sidebarTitle,
  sidebarSubTitle,
  sidebarHasDelimiter,
  sidebarActionButtons,
  sidebarOnClose,
  scrollable = true, //useful when a page handles it's own scrolling; ex the deal document detail pages
  showDealNav = true,
  children,
}: Props) => {
  const [documentWidth, setDocumentWidth] = useState<number>(defaultPageWidth);
  const [headerHeight, setHeaderHeight] = useState<number>(defaultHeaderHeight);

  const context = useContext(PopupsContext);

  useEffect(() => {
    context.subHeaderVisibilitySwitch(showDealNav);
  }, [showDealNav]);

  //set page width on mount and resize
  useEffect(() => {
    const handleResize = () => {
      const windowWidth = window.innerWidth;

      if (windowWidth > 1150) {
        //scale document size if window is large
        setDocumentWidth(windowWidth - 559);
      } else {
        //else set minimum document size
        setDocumentWidth(600);
      }
    };

    // Call handleResize on component mount
    handleResize();

    // Add event listener for window resize
    window.addEventListener("resize", handleResize);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const theme = useTheme();

  const dealPageContainer: SxProps<Theme> = {
    height: "auto",
    paddingTop: "20px",
    paddingLeft: "40px",
    paddingRight: "40px",
    display: "flex",
    flexDirection: "row",
    flex: 1,
    color: theme.palette.tertiary.darker,
  };

  const pageMainContainer: SxProps<Theme> = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    paddingTop: "1.5px",
  };

  const pageMainContentContainer: SxProps<Theme> = {
    overflowY: scrollable ? "auto" : "hidden",
    maxHeight: "85vh",
    marginRight: "5px",
    "&::-webkit-scrollbar": {
      //hide scrollbar
      display: "none",
    },
  };

  const pageSidebarContainer: SxProps<Theme> = {
    minWidth: "380px",
    display: "flex",
    flexDirection: "column",
    height: "100%",
  };

  console.log();

  return (
    <PageHeaderHeightContext.Provider value={headerHeight}>
      <PageWidthContext.Provider value={documentWidth}>
        <Box sx={dealPageContainer}>
          <Box sx={pageMainContainer}>
            {(title ||
              leftHeaderAction ||
              rightHeaderAction ||
              actionButtons) && (
              <DealsPageHeader
                title={title}
                leftAction={leftHeaderAction}
                rightAction={rightHeaderAction}
                actionButtons={actionButtons}
                onHeightChange={(height) => {
                  setHeaderHeight(height);
                }}
              />
            )}
            <Box sx={pageMainContentContainer}>{children}</Box>
          </Box>
          {sidebar && (
            <Box sx={pageSidebarContainer}>
              <RightPanel
                title={sidebarTitle}
                subTitle={sidebarSubTitle}
                isDelimiter={sidebarHasDelimiter}
                actionButtons={sidebarActionButtons}
                onClose={sidebarOnClose}
              >
                {sidebar}
              </RightPanel>
            </Box>
          )}
        </Box>
      </PageWidthContext.Provider>
    </PageHeaderHeightContext.Provider>
  );
};
